import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Users, Shield, Briefcase, CheckCircle, AlertCircle, Settings, BarChart, FileText, Plane, Mail, Calendar } from 'lucide-react';

const UserManual = () => {
  const [userRole, setUserRole] = useState('agency');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isAgency, setIsAgency] = useState(true);

  // Safely get user from localStorage
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const role = user.role || 'agency';
        setUserRole(role);
        setIsAdmin(role === 'admin');
        setIsStaff(role === 'staff');
        setIsAgency(role === 'agency');
      }
    } catch (error) {
      console.error('Error loading user from localStorage:', error);
    }
  }, []);

  return (
    <div className="container mx-auto p-6" style={{ background: '#0A1128' }}>
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex justify-center mb-4">
          <BookOpen className="h-16 w-16" style={{ color: '#4A5A8A' }} />
        </div>
        <h1 className="text-4xl font-bold mb-2" style={{ color: '#E8E8E8', fontFamily: 'Century Gothic' }}>
          User Manual
        </h1>
        <p className="text-lg" style={{ color: '#B0B0B0' }}>
          Comprehensive guide to using the Airport VIP Services Management System
        </p>
        <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>Updated: 28 December 2025</Badge>
      </div>

      {/* Role-based Tabs */}
      <Tabs defaultValue={isAdmin ? "admin" : isStaff ? "staff" : "agency"} className="w-full">
        <TabsList className="grid w-full" style={{ 
          gridTemplateColumns: isAdmin ? 'repeat(3, 1fr)' : '1fr',
          background: '#1E2A4A',
          borderColor: '#4A5A8A'
        }}>
          {(isAgency || isAdmin) && (
            <TabsTrigger value="agency" style={{ color: '#E8E8E8' }}>
              <Briefcase className="h-4 w-4 mr-2" />
              Agency Manual
            </TabsTrigger>
          )}
          {(isStaff || isAdmin) && (
            <TabsTrigger value="staff" style={{ color: '#E8E8E8' }}>
              <Users className="h-4 w-4 mr-2" />
              Staff Manual
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="admin" style={{ color: '#E8E8E8' }}>
              <Shield className="h-4 w-4 mr-2" />
              Admin Manual
            </TabsTrigger>
          )}
        </TabsList>

        {/* AGENCY MANUAL */}
        {(isAgency || isAdmin) && (
          <TabsContent value="agency">
            <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
              <CardHeader>
                <CardTitle style={{ color: '#E8E8E8', fontFamily: 'Century Gothic' }}>
                  Agency User Guide
                </CardTitle>
                <CardDescription style={{ color: '#B0B0B0' }}>
                  Complete guide for agency users to manage flights and services
                </CardDescription>
              </CardHeader>
              <CardContent style={{ color: '#E8E8E8' }} className="space-y-8">
                
                {/* Section 1: Getting Started */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <CheckCircle className="h-6 w-6" />
                    1. Getting Started
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Login & Registration</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Navigate to the login page and click "Register" for first-time users</li>
                        <li>Enter your email (must be company domain - no Gmail/Outlook/etc.)</li>
                        <li>Receive 6-digit verification code via email (expires in 10 minutes)</li>
                        <li>Complete registration form with your agency details</li>
                        <li>Wait for admin approval before accessing the system</li>
                        <li>Once approved, log in with your credentials</li>
                        <li>Accept GDPR policy (shown on every login)</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#4A5A8A' }}>All registrations are logged and require admin approval</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Dashboard Overview</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Total Flights:</strong> View all your flights (click to open Planning page)</li>
                        <li><strong>Total Passengers:</strong> Sum of passengers across all flights</li>
                        <li><strong>Pending Invoices:</strong> Invoices awaiting payment (click to view)</li>
                        <li><strong>Today's Flights:</strong> Flights scheduled for today</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 2: Creating Flights */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Plane className="h-6 w-6" />
                    2. Creating Flight Requests
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Manual Flight Entry</h4>
                      <ol className="list-decimal list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Click "Create Flight" button</li>
                        <li><strong>Flight Prefix:</strong> Enter 2-character airline code (auto-uppercase)</li>
                        <li><strong>Flight Number Only:</strong> Enter numbers only (auto-uppercase)</li>
                        <li><strong>Airline:</strong> Auto-recognized from prefix (read-only once filled)</li>
                        <li><strong>Flight Type:</strong> Select Arrival or Departure</li>
                        <li><strong>Flight Date & Time:</strong> Select from calendar</li>
                        <li><strong>Departure/Arrival Airport:</strong> Select from dropdown</li>
                        <li><strong>Passenger Count:</strong> Enter number of passengers</li>
                        <li><strong>Group Info (NEW):</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Group Name</li>
                            <li>Tour Leader Name</li>
                            <li>Tour Leader Phone</li>
                            <li>Emergency Contact</li>
                          </ul>
                        </li>
                        <li>Click "Create" - flight goes to "Pending" status</li>
                        <li><strong>Service Selection:</strong> After creation, select 2h or 3h Basic Assistance</li>
                      </ol>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Flight info auto-converts to uppercase</Badge>
                      <Badge className="mt-2 ml-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Airline auto-fills from prefix</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Service Hours Selection</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>After creating a flight, you'll see a service hours popup</li>
                        <li>Choose between <strong>2-hour</strong> or <strong>3-hour</strong> Basic Assistance service</li>
                        <li>Pricing differs based on selection</li>
                        <li>This selection affects the final invoice amount</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Editing Services & Invoiced Flights (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Before Invoice:</strong> You can freely add, edit, or remove services</li>
                        <li><strong>After Invoice Created:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>❌ Cannot add new services</li>
                            <li>❌ Cannot edit existing services</li>
                            <li>❌ Cannot delete services</li>
                            <li>Error message: "Services cannot be added/deleted to invoiced flights. Please contact admin."</li>
                          </ul>
                        </li>
                        <li><strong>Why?</strong> Maintains invoice integrity and prevents billing discrepancies</li>
                        <li><strong>Need Changes?</strong> Contact admin - only admins can modify invoiced flights</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#EF4444', color: '#FFFFFF' }}>IMPORTANT: Invoice protection</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Flight Approval Process</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>All agency-created flights start with "Pending" status</li>
                        <li>Admin reviews and approves/rejects flights</li>
                        <li>Once approved, flights appear in Planning and Reports</li>
                        <li>You can view all your flights (pending, approved, rejected) in Flights page</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 3: Managing Your Agency */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Briefcase className="h-6 w-6" />
                    3. Managing Your Agency Profile
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Viewing Agency Information</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Navigate to "Agencies" page</li>
                        <li>View complete agency details including:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Contact Information (name, email, phone)</li>
                            <li>Full Address</li>
                            <li>VAT Number</li>
                            <li>Banking Details (IBAN, SWIFT, Bank Name, Account Number)</li>
                            <li>Service Preferences</li>
                          </ul>
                        </li>
                        <li>Contact admin for any updates to your profile</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Full agency details now visible to all users</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Managing Users & Passwords (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Click "View Users" to see all users associated with your agency</li>
                        <li>View user information with your agency's email domain</li>
                        <li><strong>Change Your Own Password:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Find your user account in the list</li>
                            <li>Click "Edit My Password" button</li>
                            <li>Enter new password (minimum 6 characters)</li>
                            <li>Confirm password change</li>
                            <li>Use new password on next login</li>
                          </ul>
                        </li>
                        <li>You can only change your own password, not other users</li>
                        <li>For security, passwords are encrypted and cannot be viewed</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Self-service password management!</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 4: Invoices */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <FileText className="h-6 w-6" />
                    4. Invoices & Payments
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Viewing Invoices via Agency Statements (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Access "Accounting" → "Agency Statements" from menu</li>
                        <li>View all invoices generated for your agency</li>
                        <li>Filter by: Invoice Number, Date Range, Payment Status</li>
                        <li>See invoice details:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Invoice Number and Date</li>
                            <li>Total Amount</li>
                            <li>Payment Status (Unpaid/Paid)</li>
                            <li>Payment Date (if paid)</li>
                          </ul>
                        </li>
                        <li>Download PDF invoices for your records</li>
                        <li><strong>Restrictions:</strong> You can only view your own agency's invoices</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Dedicated invoice management page!</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Invoice Details</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Invoice includes: Agency details, Flight details, Services, Total amount</li>
                        <li>Shows VAT exemption (Article 138 Directive 2006/112/EC)</li>
                        <li>Professional PDF format with company logo</li>
                        <li>Due date: 8 days from invoice date</li>
                        <li>Payment instructions and bank details included</li>
                        <li>Detailed breakdown of services and costs</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 5: Statistics */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <BarChart className="h-6 w-6" />
                    5. Statistics & Reports
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Agency Statistics</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Access "Accounting" → "Statistics"</li>
                        <li>View monthly statistics for your agency</li>
                        <li>Filter by month and year</li>
                        <li>See totals for: Flights, Passengers, Revenue</li>
                        <li>Track performance over time</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 6: APP Settings */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Settings className="h-6 w-6" />
                    6. APP Settings
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Agency Users Management (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Access "APP Settings" → "Users"</li>
                        <li>View all users in your agency (read-only)</li>
                        <li>See user details:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Full Name, Email, Role, Status</li>
                            <li>Your user marked with "You" badge</li>
                          </ul>
                        </li>
                        <li><strong>Change Your Password:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Click "Change My Password" button</li>
                            <li>Enter new password (6+ characters, 1 digit, 1 special character)</li>
                            <li>Confirm password and save</li>
                          </ul>
                        </li>
                        <li><strong>Restrictions:</strong> View-only access - cannot create, edit, or delete users</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#10B981', color: '#FFFFFF' }}>NEW: November 2025</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Login History (UPDATED)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Access "APP Settings" → "Login History"</li>
                        <li>View login attempts for <strong>all users in your agency</strong></li>
                        <li>Automatic filtering by your agency ID (not just email domain)</li>
                        <li>See: Timestamp, Email, Status (Success/Failed), IP Address, Device</li>
                        <li>Monitor account security for your entire team</li>
                        <li>Filter by date range or specific user</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>UPDATED: Agency-wide filtering</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 7: Operational Reports (NEW) */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <FileText className="h-6 w-6" />
                    7. Operational Reports (View-Only)
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Viewing Reports</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Access "Reports" from the main menu</li>
                        <li>View operational reports for your agency's flights</li>
                        <li>Filter by: Date, Flight Number, Status</li>
                        <li>Available actions:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>✅ <strong>View:</strong> See detailed report information</li>
                            <li>✅ <strong>PDF:</strong> Download report as PDF document</li>
                            <li>❌ <strong>Cannot Edit:</strong> Reports are read-only for agencies</li>
                            <li>❌ <strong>Cannot Email:</strong> Cannot send reports via email</li>
                            <li>❌ <strong>Cannot Delete:</strong> Cannot remove reports</li>
                            <li>❌ <strong>Cannot Create:</strong> Cannot create new reports</li>
                          </ul>
                        </li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#10B981', color: '#FFFFFF' }}>NEW: Read-only access</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Report Information</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Flight details: Number, Date, Time, Aircraft type</li>
                        <li>Passenger information and count</li>
                        <li>Services provided and timing</li>
                        <li>Agent information and duty hours</li>
                        <li>Special notes and observations</li>
                        <li>Gate information and parking details</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Tips & Best Practices */}
                <div className="mt-8 p-4 rounded-lg" style={{ background: 'rgba(74, 159, 255, 0.1)', borderLeft: '4px solid #4A9FFF' }}>
                  <h4 className="text-xl font-semibold mb-2" style={{ color: '#4A9FFF' }}>💡 Tips & Best Practices</h4>
                  <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                    <li>Create flights at least 48 hours in advance for smooth processing</li>
                    <li>Double-check passenger count and flight details before submission</li>
                    <li>Ensure all group information is accurate for better service</li>
                    <li>Monitor pending flights status regularly</li>
                    <li>Review invoices upon receipt and report any discrepancies immediately</li>
                    <li>Keep your contact information up to date</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* STAFF MANUAL */}
        {(isStaff || isAdmin) && (
          <TabsContent value="staff">
            <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
              <CardHeader>
                <CardTitle style={{ color: '#E8E8E8', fontFamily: 'Century Gothic' }}>
                  Staff User Guide
                </CardTitle>
                <CardDescription style={{ color: '#B0B0B0' }}>
                  Operational guide for staff members handling VIP services
                </CardDescription>
              </CardHeader>
              <CardContent style={{ color: '#E8E8E8' }} className="space-y-8">
                
                {/* Section 1: Getting Started */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <CheckCircle className="h-6 w-6" />
                    1. Getting Started
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Login & Dashboard</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Log in with your staff credentials</li>
                        <li>Accept GDPR policy on login</li>
                        <li>Dashboard shows overview of system activity</li>
                        <li>Access "My Flights" to see your assigned duties</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 2: My Flights (Planning) */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Calendar className="h-6 w-6" />
                    2. My Flights (Planning)
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Viewing Your Assignments</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Navigate to "My Flights" (renamed from Global Planning for staff)</li>
                        <li>See all flights where you're assigned as operational staff</li>
                        <li>View flight details: Date, Time, Route, Passengers, Services</li>
                        <li>Filter by date or search for specific flights</li>
                        <li>Click on a flight to see detailed information</li>
                        <li><strong>View-Only Access:</strong> You cannot create, edit, or delete flights</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>RESTRICTION: View-only access for staff</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Flight Information</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Flight number with link to live tracking (planefinder.net)</li>
                        <li>Client/Agency information</li>
                        <li>Group details: Name, Tour Leader, Emergency Contact</li>
                        <li>Service requirements and special requests</li>
                        <li>Attached documents (briefings, passenger lists)</li>
                        <li><strong>Note:</strong> Price information is hidden from staff view</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Access Restrictions (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>"Add Flight" button:</strong> Hidden - staff cannot create flights</li>
                        <li><strong>"Import Excel" button:</strong> Hidden - staff cannot import flights</li>
                        <li><strong>Edit/Delete actions:</strong> Not available for staff users</li>
                        <li><strong>Pricing information:</strong> Hidden from flight details</li>
                        <li><strong>Data filtering:</strong> Only see flights assigned to you</li>
                        <li>Contact admin if you need to create or modify flight information</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Enhanced permission controls</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 3: Operational Reports */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <FileText className="h-6 w-6" />
                    3. Creating Operational Reports
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>After Service Completion</h4>
                      <ol className="list-decimal list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Navigate to "Reports" page</li>
                        <li>Click "Create Report"</li>
                        <li><strong>Report Date:</strong> Auto-fills with today's date (editable)</li>
                        <li><strong>Select Flight:</strong> Choose from flights on selected date</li>
                        <li><strong>Auto-filled fields:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Duty Start Time (flight time - 2 hours)</li>
                            <li>Duty End Time (flight scheduled time)</li>
                            <li>Actual Departure Time (from flight ATD or time)</li>
                            <li>Agent Name (from flight assignment)</li>
                            <li>Passenger Count</li>
                          </ul>
                        </li>
                        <li><strong>Actual Departure Time:</strong> MANDATORY - adjust if needed</li>
                        <li>Update actual passenger count if different</li>
                        <li>Select no-show passengers from dropdown list</li>
                        <li>Enter go-show passengers (if any)</li>
                        <li>Enter gate number and connection info</li>
                        <li>Add operational notes</li>
                        <li>Select service completion status</li>
                        <li>Submit report</li>
                      </ol>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Smart auto-fill saves time!</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Report Actions</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Download PDF report</li>
                        <li>Edit report details if needed</li>
                        <li>View report history</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Tips for Staff */}
                <div className="mt-8 p-4 rounded-lg" style={{ background: 'rgba(74, 159, 255, 0.1)', borderLeft: '4px solid #4A9FFF' }}>
                  <h4 className="text-xl font-semibold mb-2" style={{ color: '#4A9FFF' }}>💡 Operational Tips</h4>
                  <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                    <li>Check your assignments daily in "My Flights"</li>
                    <li>Review flight details and special requests before duty</li>
                    <li>Submit operational reports within 24 hours of service completion</li>
                    <li>Be accurate with passenger counts and no-show information</li>
                    <li>Document any issues or special circumstances in notes</li>
                    <li>Contact admin for any assignment conflicts or questions</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ADMIN MANUAL */}
        {isAdmin && (
          <TabsContent value="admin">
            <Card style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
              <CardHeader>
                <CardTitle style={{ color: '#E8E8E8', fontFamily: 'Century Gothic' }}>
                  Admin User Guide
                </CardTitle>
                <CardDescription style={{ color: '#B0B0B0' }}>
                  Complete administrative manual for system management
                </CardDescription>
              </CardHeader>
              <CardContent style={{ color: '#E8E8E8' }} className="space-y-8">
                
                {/* Section 1: Dashboard & Overview */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <BarChart className="h-6 w-6" />
                    1. Dashboard & System Overview
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Dashboard Features</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Total Flights:</strong> Click to view all flights in Planning</li>
                        <li><strong>Total Passengers:</strong> Click to view Flights page</li>
                        <li><strong>Pending Invoices:</strong> Click to view unpaid invoices</li>
                        <li><strong>Today's Flights:</strong> Click to view today's schedule</li>
                        <li>All cards are interactive - click for detailed views</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Statistics Page</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>View comprehensive system statistics</li>
                        <li>Filter by month, year, and agency</li>
                        <li>Click "Refresh Data" to update statistics</li>
                        <li>See "Last updated" timestamp</li>
                        <li>Export data for reporting</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Refresh button for real-time data</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 2: Flight Management */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Plane className="h-6 w-6" />
                    2. Flight Management
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Creating Flights</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Click "Add Flight" button</li>
                        <li><strong>Flight Prefix (NEW):</strong> 2-character IATA code (auto-uppercase)
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>System detects O/0 ambiguity (e.g., "5O" vs "50")</li>
                            <li>Shows confirmation dialog for ambiguous codes</li>
                          </ul>
                        </li>
                        <li><strong>Flight Number Only:</strong> Numbers part only (auto-uppercase)</li>
                        <li><strong>Airline Recognition:</strong> Auto-fills from prefix database</li>
                        <li><strong>New Group Information Fields:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Group Name</li>
                            <li>Tour Leader Name & Phone</li>
                            <li>Emergency Contact</li>
                          </ul>
                        </li>
                        <li>Select services (2h or 3h Basic Assistance popup)</li>
                        <li>Admin-created flights are auto-approved</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Enhanced flight data capture</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Pending Tasks Management (UPDATED)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Access "Pending Tasks" page (admin-only)</li>
                        <li><strong>Tabbed Interface:</strong> Manage all pending approvals in one place
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li><strong>Pending Flights:</strong> Review and approve/reject agency-submitted flights</li>
                            <li><strong>Pending Agencies:</strong> Approve or reject new agency registrations</li>
                            <li><strong>Pending Users:</strong> Approve or reject new user registrations</li>
                            <li><strong>Approval History:</strong> View log of all past approve/reject actions</li>
                          </ul>
                        </li>
                        <li>Each tab displays items with "Approve" and "Reject" functionality</li>
                        <li>Cards styled consistently with Dashboard page</li>
                        <li>History tab shows: User, Action, Details, Timestamp</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Centralized approval hub!</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Editing Flights</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Click edit icon on any flight</li>
                        <li>Dialog title changes to "Editing Flight"</li>
                        <li>All fields are pre-filled with current data</li>
                        <li><strong>Service Hours Selection (NEW):</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Shows current Basic Assistance selection (2h or 3h)</li>
                            <li>Radio buttons to change selection</li>
                            <li>Price updates automatically when changed</li>
                            <li>Warning shown that price will update</li>
                          </ul>
                        </li>
                        <li>Agency name displayed in golden color on flight cards</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Edit service hours with auto-price update</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Excel Import (UPDATED)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Click "Import Flights from Excel"</li>
                        <li><strong>Supported File Formats (NEW):</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>✅ .xlsx (Modern Excel format)</li>
                            <li>✅ .xls (Old Excel 97-2003 format)</li>
                            <li>✅ .csv (Comma-separated values)</li>
                          </ul>
                        </li>
                        <li>Required columns: DATE, AGENCY, FLT NUMBER, DEP, DES, DEP PAX, ATD</li>
                        <li>System auto-creates agencies if they don't exist</li>
                        <li>All imported flights start as "Pending"</li>
                        <li>Review and approve imported flights</li>
                        <li>Import logs show success/error count for each file</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#10B981', color: '#FFFFFF' }}>UPDATED: .xls & .csv support</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Refresh Price Feature (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Click "Refresh Price" button on any flight</li>
                        <li><strong>Smart Price Selection:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>System checks available 2h and 3h service options</li>
                            <li>Shows price selection dialog if multiple options available</li>
                            <li>Auto-refreshes with single option if only one available</li>
                            <li>Displays service duration, unit price, parking fee, and total</li>
                          </ul>
                        </li>
                        <li><strong>Service Replacement (Fixed):</strong> Replaces existing service instead of adding duplicate</li>
                        <li><strong>Auto-Refresh UI:</strong> Flight list updates automatically after price selection</li>
                        <li>Updates both service details and total amount instantly</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Enhanced price refresh with selection dialog</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Flight Sorting Options (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Default Sorting:</strong> Nearest date first (closest to today)</li>
                        <li><strong>Sort Options:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>🕐 <strong>Nearest First:</strong> Flights closest to today's date appear first</li>
                            <li>⬇️ <strong>Newest First:</strong> Latest dates appear first</li>
                            <li>⬆️ <strong>Oldest First:</strong> Earliest dates appear first</li>
                          </ul>
                        </li>
                        <li>Click sort button to cycle through options</li>
                        <li>Applied to both Flight Management and Global Planning pages</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Smart date-based sorting</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 3: Services Management */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Briefcase className="h-6 w-6" />
                    3. Services Management
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Managing Services</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Navigate to "Accounting" → "Services"</li>
                        <li>Create airport-specific services</li>
                        <li>Set different prices for 2h and 3h assistance</li>
                        <li>Configure agency-specific pricing</li>
                        <li><strong>Service Categories:</strong> Basic Assistance or Extra Services</li>
                        <li><strong>Cascading Updates (NEW):</strong> Editing a service automatically updates:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>All flights using that service</li>
                            <li>All operational reports</li>
                            <li>Service name, price, and parking fee</li>
                          </ul>
                        </li>
                        <li><strong>Sync All Services (NEW):</strong> Manual button to sync service data across all flights</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Automatic cascading updates!</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 4: Operational Reports */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <FileText className="h-6 w-6" />
                    4. Operational Reports Management
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Creating Reports</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Same process as staff, with smart auto-fill features</li>
                        <li><strong>Auto-filled fields:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Report Date (today's date)</li>
                            <li>Duty Start Time (flight time - 2 hours)</li>
                            <li>Duty End Time (flight scheduled time)</li>
                            <li>Actual Departure Time (from flight ATD)</li>
                          </ul>
                        </li>
                        <li>All fields remain editable for adjustments</li>
                        <li>Actual Departure Time is mandatory</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Smart auto-fill for faster reporting</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Sending Reports by Email (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Click "Send by Email" button on any report</li>
                        <li>System automatically:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Generates complete PDF with all report data</li>
                            <li>Includes company header with logo</li>
                            <li>Shows all 19 report fields professionally formatted</li>
                            <li>Sends to agency email address</li>
                          </ul>
                        </li>
                        <li><strong>Email Format:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Subject: "Operational Flight Report – [Flight Number] – [Date]"</li>
                            <li>Professional body with flight details</li>
                            <li>Company signature with email only</li>
                          </ul>
                        </li>
                        <li><strong>Email History:</strong> Track all sent emails with:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Sent timestamp</li>
                            <li>Recipient email</li>
                            <li>Sender name</li>
                          </ul>
                        </li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: One-click email delivery!</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Report Form Improvements (FIXED)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Duplicate Group Information Fixed:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Removed duplicate "Group & Contact Information" section</li>
                            <li>Form now shows Group Name, Tour Leader Name, Phone, Emergency Contact only once</li>
                            <li>Fields properly sync with flight data</li>
                            <li>Cleaner, more intuitive form layout</li>
                          </ul>
                        </li>
                        <li><strong>No-Show Passenger Entry Enhanced:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Improved textarea interaction for multi-line passenger entry</li>
                            <li>Pressing Enter now creates new line without disappearing</li>
                            <li>Empty lines preserved during typing</li>
                            <li>Cleanup happens when finished editing (onBlur)</li>
                            <li>Smoother user experience for passenger list entry</li>
                          </ul>
                        </li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#10B981', color: '#FFFFFF' }}>FIXED: Form usability issues</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>PDF Reports</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Enhanced PDF format with company/client headers</li>
                        <li>Golden line separator</li>
                        <li>Complete flight and operational data</li>
                        <li>Group and contact information included</li>
                        <li>Professional formatting for client delivery</li>
                        <li><strong>Passenger List PDF Export (FIXED):</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Fixed black squares/dots in passenger names</li>
                            <li>Clean character encoding for special characters</li>
                            <li>Proper space handling in passenger names</li>
                            <li>Professional passenger list layout with flight details</li>
                          </ul>
                        </li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#10B981', color: '#FFFFFF' }}>FIXED: PDF character encoding</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 5: ATT Settings */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Settings className="h-6 w-6" />
                    5. ATT Settings (Company Settings)
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Company Settings</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Configure company information (name, address, contact)</li>
                        <li>Upload company logo (appears on reports and PDFs)</li>
                        <li>Set banking details for invoices</li>
                        <li>VAT number and registration details</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Agents on Duty</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Create and manage operational staff profiles</li>
                        <li>Assign agents to flights</li>
                        <li>Track agent availability and schedules</li>
                        <li>Prevent duplicate agent names</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>User Management</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Approve/reject new user registrations</li>
                        <li>View registration verification logs</li>
                        <li>Manage user roles and permissions</li>
                        <li>Reset user passwords if needed</li>
                        <li>Deactivate user accounts</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Email Automation (UPDATED)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Default Email Server Configuration:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>SMTP Server (e.g., send.one.com)</li>
                            <li>SMTP Port (default: 465)</li>
                            <li>Email Address (sender email)</li>
                            <li>Password (stored securely)</li>
                            <li>SSL/TLS toggle</li>
                            <li>CC Email (optional - copy all emails)</li>
                          </ul>
                        </li>
                        <li><strong>Invoice Email Server Configuration (NEW):</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Separate SMTP server specifically for invoices</li>
                            <li>Invoice SMTP Server & Port</li>
                            <li>Invoice Email Address (e.g., invoices@company.com)</li>
                            <li>Invoice Password</li>
                            <li>Invoice BCC Email - automatically BCC all invoice emails</li>
                            <li>Optional - leave empty to use default server</li>
                          </ul>
                        </li>
                        <li><strong>Why Separate Invoice Server?</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Professional separation (invoices from accounting@)</li>
                            <li>Different email provider for accounting</li>
                            <li>Automatic BCC to accounting department</li>
                            <li>Track invoice emails separately</li>
                          </ul>
                        </li>
                        <li><strong>Sending Invoices:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Choose "Default Email Server" or "Invoice Email Server"</li>
                            <li>BCC automatically added when using invoice server</li>
                            <li>Recipient never sees BCC address</li>
                          </ul>
                        </li>
                        <li>Test email configuration before use</li>
                        <li>Both configurations have separate save buttons</li>
                        <li>Passwords optional when updating (keeps existing if empty)</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#10B981', color: '#FFFFFF' }}>NEW: Invoice-specific SMTP + BCC</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Email Templates (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Centralized template management</strong></li>
                        <li><strong>Operational Flight Report Template:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Customize subject and body</li>
                            <li>Use variables: <code>{`{flight_number}`}</code>, <code>{`{flight_date}`}</code>, <code>{`{client_name}`}</code>, etc.</li>
                            <li>Professional format for client communication</li>
                          </ul>
                        </li>
                        <li><strong>Registration Verification Template:</strong> 6-digit code email</li>
                        <li><strong>Welcome Email Template:</strong> Post-approval welcome message</li>
                        <li>Preview templates before saving</li>
                        <li>Reset to default option</li>
                        <li>All templates support variable substitution</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Customize all email communications!</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 6: APP Settings */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Settings className="h-6 w-6" />
                    6. APP Settings (Application Configuration)
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Airlines Data Management (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Manage IATA airline codes database</strong></li>
                        <li>Create, edit, and delete airline entries</li>
                        <li>Update airline prefix and full name</li>
                        <li>View flight counts per airline</li>
                        <li><strong>Click flight count</strong> to see all flights for that airline</li>
                        <li>Edit flights directly from airline view</li>
                        <li>Filter airlines: "Show Only Used" toggle</li>
                        <li>Auto-refresh statistics</li>
                        <li><strong>Prefix editing:</strong> System validates and updates all related flights</li>
                        <li>Search and filter capabilities</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Complete airline database control!</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Airports Management</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Add and manage airport codes</li>
                        <li>Set airport-specific service configurations</li>
                        <li>Define 2h/3h assistance availability per airport</li>
                        <li>Configure parking fees by airport</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Login History (UPDATED)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>View all user login attempts system-wide</li>
                        <li>Filter by: Agency, Email domain, Date range</li>
                        <li>See: Timestamp, Email, Role, Status (Success/Failed)</li>
                        <li>Clear history button (with audit logging)</li>
                        <li>Export login data for security audits</li>
                        <li>Monitor system access patterns</li>
                        <li><strong>Log Off All Users (NEW):</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Click "Log Off All Users" button (admin-only)</li>
                            <li>Invalidates all active sessions for non-admin users</li>
                            <li>Admin accounts remain logged in</li>
                            <li>Forces all users to re-authenticate</li>
                            <li>Useful for security incidents or policy changes</li>
                          </ul>
                        </li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Log Off All Users feature!</Badge>
                    </div>
                  </div>
                </div>

                {/* Section 7: Agency Management */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <Briefcase className="h-6 w-6" />
                    7. Agency Management & Invoicing
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Managing Agencies</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Create new agency profiles</li>
                        <li>Edit existing agency information</li>
                        <li>Configure agency-specific service pricing</li>
                        <li>Set service preferences per agency</li>
                        <li><strong>Full agency details now visible:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Contact information (name, email, phone, address)</li>
                            <li>VAT number</li>
                            <li>Complete banking details (IBAN, SWIFT, Bank Name, Account)</li>
                            <li>Service preferences</li>
                          </ul>
                        </li>
                        <li>Prevent duplicate agency names and emails</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Full agency transparency</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Creating Agency Users (UPDATED)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Two Ways to Create Agency Users:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li><strong>Method 1: From Agencies Page</strong>
                              <ul className="list-disc list-inside ml-6 mt-1">
                                <li>Go to Agencies page</li>
                                <li>Find agency card → Click "Create User"</li>
                                <li>Agency automatically selected</li>
                                <li>Enter: Email, Full Name, Password</li>
                                <li>User created with proper agency link</li>
                              </ul>
                            </li>
                            <li><strong>Method 2: From User Management (NEW)</strong>
                              <ul className="list-disc list-inside ml-6 mt-1">
                                <li>Go to User Management page</li>
                                <li>Click "Create New User"</li>
                                <li>Select Role: "Agency"</li>
                                <li>✅ Agency dropdown appears automatically</li>
                                <li>Select which agency to assign user to</li>
                                <li>Enter user details and save</li>
                              </ul>
                            </li>
                          </ul>
                        </li>
                        <li><strong>Synchronization (NEW):</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Users created from either page appear in both places</li>
                            <li>User Management shows all users with agency filter</li>
                            <li>Agencies page "View Users" shows agency-specific users</li>
                            <li>All users properly linked via agency_id field</li>
                          </ul>
                        </li>
                        <li><strong>Editing Agency Users:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Edit user details in User Management</li>
                            <li>Can change user's agency assignment via dropdown</li>
                            <li>Changes reflected immediately in both views</li>
                          </ul>
                        </li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#10B981', color: '#FFFFFF' }}>UPDATED: Synchronized user management</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Agency Deletion & Rejected Flights (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Automatic Rejected Flight Archival:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>When admin rejects a flight → automatically moved to flight_history collection</li>
                            <li>Rejected flight removed from active flights database</li>
                            <li>Full flight data preserved in history (including rejection reason, date, admin who rejected)</li>
                            <li>Agencies can view last 15 days of rejected flights in Dashboard</li>
                          </ul>
                        </li>
                        <li><strong>Enhanced Agency Deletion:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>System now checks only active flights (approved + pending)</li>
                            <li>Rejected flights don't block agency deletion anymore</li>
                            <li>Clear error messages showing flight status breakdown</li>
                            <li>Example: "Cannot delete agency: 2 active flights (1 approved, 1 pending). Please delete active flights first."</li>
                          </ul>
                        </li>
                        <li><strong>Flight History Access:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Admins can view archived rejected flights via API</li>
                            <li>Complete audit trail maintained for all rejected flights</li>
                            <li>Historical data includes rejection metadata and migration notes</li>
                          </ul>
                        </li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Smart flight archival & agency deletion</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Agency Statements & Invoice Management (NEW)</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Access "Accounting" → "Agency Statements"</li>
                        <li><strong>Centralized invoice management for all agencies</strong></li>
                        <li>Filter invoices by:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Agency (dropdown selection)</li>
                            <li>Invoice Number (search)</li>
                            <li>Date Range (from/to)</li>
                            <li>Payment Status (All/Unpaid/Paid)</li>
                          </ul>
                        </li>
                        <li><strong>Invoice Actions:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>View invoice PDF</li>
                            <li>Mark as paid (with editable amount and invoice number)</li>
                            <li>Edit invoice details (amount, number)</li>
                            <li>Mark as unpaid (reverse payment status)</li>
                            <li>Delete unpaid invoices</li>
                          </ul>
                        </li>
                        <li><strong>Payment Tracking:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Payment status indicator (Unpaid/Paid)</li>
                            <li>Payment date recorded</li>
                            <li>Editable total amount</li>
                            <li>Invoice number management</li>
                          </ul>
                        </li>
                        <li>Invoice numbering format: YEAR + "010" + COUNT (e.g., 2025010097)</li>
                        <li>Invoices automatically sort by invoice number</li>
                        <li>Historical invoices included (can be imported from Excel)</li>
                      </ul>
                      <Badge className="mt-2" style={{ background: '#FFD700', color: '#0A1128' }}>NEW: Complete invoice lifecycle management!</Badge>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Creating Invoices</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Navigate to "Accounting" → "Invoices"</li>
                        <li>Click "Create Invoice" button</li>
                        <li>Select agency from dropdown</li>
                        <li>Select uninvoiced flights (system filters out already invoiced flights)</li>
                        <li><strong>Historical Flight Filtering:</strong>
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Flights up to specific dates are marked as invoiced</li>
                            <li>Only recent uninvoiced flights appear in selection</li>
                            <li>Prevents duplicate invoicing of historical data</li>
                          </ul>
                        </li>
                        <li>Invoice auto-generates with:
                          <ul className="list-disc list-inside ml-4 mt-2">
                            <li>Sequential invoice number</li>
                            <li>Flight details and services</li>
                            <li>Total amount calculation</li>
                            <li>VAT exemption notice</li>
                            <li>Due date (8 days from invoice date)</li>
                          </ul>
                        </li>
                        <li>Download PDF invoice</li>
                        <li>Send to agency via email</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Bulk Invoice Management</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Mark multiple historical flights as invoiced via backend script</li>
                        <li>Import historical invoices from Excel (payment status tracking)</li>
                        <li>Link specific flights to existing invoices</li>
                        <li>Dashboard shows unpaid invoices count from Agency Statements</li>
                        <li>Statistics page calculates revenue from invoice data</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 8: Data Maintenance */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: '#4A9FFF' }}>
                    <AlertCircle className="h-6 w-6" />
                    8. System Maintenance & Data Quality
                  </h3>
                  
                  <div className="space-y-4 ml-8">
                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Service Data Consistency</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li><strong>Automatic Updates:</strong> Service edits cascade to all associated flights</li>
                        <li><strong>Manual Sync:</strong> Use "Sync All Services" button to ensure data consistency</li>
                        <li>Regular data validation recommended</li>
                        <li>Monitor for duplicate services</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Flight Prefix Management</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>System validates 2-character IATA codes</li>
                        <li>Detects and alerts on O/0 ambiguities</li>
                        <li>Automatic uppercase conversion</li>
                        <li>Dynamic prefix updates when editing airlines</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xl font-semibold mb-2" style={{ color: '#E8E8E8' }}>Database Cleanup</h4>
                      <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                        <li>Orphan record detection and removal</li>
                        <li>Duplicate service cleanup scripts available</li>
                        <li>Regular audit logs for tracking changes</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Best Practices for Admins */}
                <div className="mt-8 p-4 rounded-lg" style={{ background: 'rgba(74, 159, 255, 0.1)', borderLeft: '4px solid #4A9FFF' }}>
                  <h4 className="text-xl font-semibold mb-2" style={{ color: '#4A9FFF' }}>💡 Admin Best Practices</h4>
                  <ul className="list-disc list-inside space-y-2" style={{ color: '#B0B0B0' }}>
                    <li><strong>Daily Tasks:</strong> Review and approve pending flights, check operational reports</li>
                    <li><strong>Weekly Tasks:</strong> Verify service data consistency, review login history, check statistics</li>
                    <li><strong>Monthly Tasks:</strong> Generate invoices, review agency performance, update email templates if needed</li>
                    <li><strong>Configuration:</strong> Keep company settings and email templates up to date</li>
                    <li><strong>Security:</strong> Monitor login history regularly, review user access permissions</li>
                    <li><strong>Data Quality:</strong> Run service sync periodically, validate airline data, check for duplicates</li>
                    <li><strong>Communication:</strong> Customize email templates for professional client communication</li>
                    <li><strong>Training:</strong> Ensure all staff understand the operational report auto-fill features</li>
                  </ul>
                </div>

                {/* Quick Reference */}
                <div className="mt-8 p-4 rounded-lg" style={{ background: 'rgba(255, 215, 0, 0.1)', borderLeft: '4px solid #FFD700' }}>
                  <h4 className="text-xl font-semibold mb-2" style={{ color: '#FFD700' }}>🚀 Recent Updates Quick Reference</h4>
                  <div className="grid grid-cols-2 gap-4 mt-4" style={{ color: '#B0B0B0' }}>
                    <div>
                      <h5 className="font-semibold mb-2" style={{ color: '#E8E8E8' }}>Menu Reorganization</h5>
                      <ul className="list-disc list-inside text-sm">
                        <li>ATT Settings (company-related)</li>
                        <li>APP Settings (app configuration)</li>
                        <li>Agency Statements (NEW)</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold mb-2" style={{ color: '#E8E8E8' }}>Email Features</h5>
                      <ul className="list-disc list-inside text-sm">
                        <li>Send reports via email</li>
                        <li>Customizable email templates</li>
                        <li>Email history tracking</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold mb-2" style={{ color: '#E8E8E8' }}>Flight Enhancements</h5>
                      <ul className="list-disc list-inside text-sm">
                        <li>Airline auto-recognition</li>
                        <li>O/0 ambiguity detection</li>
                        <li>Uppercase auto-conversion</li>
                        <li>Group information fields</li>
                        <li>Agency flight creation</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold mb-2" style={{ color: '#E8E8E8' }}>Report Improvements</h5>
                      <ul className="list-disc list-inside text-sm">
                        <li>Smart auto-fill (dates/times)</li>
                        <li>Mandatory actual departure</li>
                        <li>Enhanced PDF format</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold mb-2" style={{ color: '#E8E8E8' }}>Invoice Management (NEW)</h5>
                      <ul className="list-disc list-inside text-sm">
                        <li>Agency Statements page</li>
                        <li>Mark invoices as paid/unpaid</li>
                        <li>Editable amounts & numbers</li>
                        <li>Historical invoice import</li>
                        <li>Bulk flight invoicing</li>
                        <li>Delete unpaid invoices</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold mb-2" style={{ color: '#E8E8E8' }}>Permissions & Access (NEW)</h5>
                      <ul className="list-disc list-inside text-sm">
                        <li>Agency password management</li>
                        <li>Staff button restrictions</li>
                        <li>Role-based default permissions</li>
                        <li>Agency-only data filtering</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Footer */}
      <div className="mt-8 text-center" style={{ color: '#B0B0B0' }}>
        <p className="text-sm">
          For technical support or questions, contact your system administrator
        </p>
        <p className="text-xs mt-2">
          Last Updated: 21 December 2025 | Version 4.2
        </p>
      </div>
    </div>
  );
};

export default UserManual;
